import cacheManager from "lib/CacheManager";
import HybridConfig from "lib/HybridConfig";
import BaseServices from "lib/BaseServices";
import promisify from "./promisify";

const cacheParam = "_=" + new Date().valueOf().toString();
const metaPrefix = BaseServices.url("meta/");

class Services {

    constructor() {
        this.loadStereotypes = this.loadStereotypes.bind(this);
        this.cachingProxy = this.cachingProxy.bind(this);
        this.loadModelJson = this.loadModelJson.bind(this);
        this.uploadFile = this.uploadFile.bind(this);
        this.getStereotypeForEntity = this.getStereotypeForEntity.bind(this);

        this.cache = cacheManager;
        this.cacheConfig = {};
        this.cacheParam = cacheParam;
        for (const k of ["isolatePage", "hydratedRecordList"]) {
            this.cacheConfig[k] = HybridConfig.get(`cache.${k}`, false);
        }

        if (this.cacheConfig.isolatePage) {
            $(document).ajaxSend((e, jqXHR, ajaxSettings) => {
                if (ajaxSettings.type === "GET" && ajaxSettings.url.indexOf(metaPrefix) === 0) {
                    ajaxSettings.url += (ajaxSettings.url.indexOf("?") < 0 ? "?" : "&") + this.cacheParam;
                }
            });
        }
    }

    loadVersion() {
        return BaseServices.getRequest("meta/version.json");
    }

    loadUser() {
        return $.ajax({
            type: "GET",
            url: BaseServices.url("user.json"),
            dataType: "json",
            statusCode: {
                401: () => BaseServices.navigateToAuthUrl(),
            },
        });
    }

    loadExportParams(org) {
        return BaseServices.getRequest(`export/${org}/saved-params.json`);
    }

    createExportParams(org, data) {
        return BaseServices.jsonPost(
            `export/${org}/saved-params.json`,
            data,
        );
    }

    updateExportParams(org, data) {
        return BaseServices.jsonPost(
            `export/${org}/saved-params/${data.id}.json`,
            data,
            { type: "PUT" },
        );
    }

    deleteExportParams(org, id) {
        return BaseServices.jsonPost(
            `export/${org}/saved-params/${id}.json`,
            {},
            { type: "DELETE" },
        );
    }

    loadArmusScript() {
        return $.ajax({
            type: "GET",
            url: BaseServices.url(["meta", "armusscript.js"]),
            dataType: "script",
            cache: true
        });
    }

    loadStereotypes() {
        return BaseServices.getRequest("meta/stereotype.json");
    }

    loadStereotype(key){
        return BaseServices.getRequest(`meta/stereotype/${key}`);
    }

    /*
    ex: /remote/data-source/adt/sentara/search?mrn=asdf1234&arrivaldate=2014-10-30&admitdate=2014-10-30

    A web endpoint is available at /v2/remote/data-source/adt/washington/search which will return
    a list of adt/1-0/episode records or raise an exception if no metadata is available to back it.

    It accepts mrn, arrivaldate, and admitdate query parameters to search on.
    */
    searchADT(org, query){
        return BaseServices.getRequest(`remote/data-source/adt/${org}/search`, query);
    }

    searchHemo(org, query){
        return BaseServices.getRequest(`remote/data-source/hemo/${org}/search`, query);
    }

    searchEcmoEmr(org, query){
        return BaseServices.getRequest(`remote/data-source/ecmoemr/${org}/search`, query);
    }

    searchEntity(query, sorts, fqen, pageSize, page, onlyEnabledRecords = false, owners) {
        if (pageSize == null) { pageSize = 20; }
        if (page == null) { page = 1; }
        return BaseServices.getRequest(["search", fqen], {
            q: query,
            o: sorts,
            ps: pageSize,
            p: page,
            oe: onlyEnabledRecords,
            owners,
        });
    }

    // @param {function} work - must return a promise.
    cachingProxy(enabled, region, key, work, forceReload) {
        if (forceReload == null) { forceReload = false; }
        if (enabled && !forceReload && this.cache.has(region, key)) {
            return this.cache.get(region, key);
        } else {
            const p = work();
            if (enabled) {
                this.cache.put(region, key, p);
            }
            return p;
        }
    }

    getByStereotype(org, stereotype){
        // @RequestMapping(value = "/{org}/{stereotype}", method = GET)
        return BaseServices.getRequest([org, stereotype]);
    }

    loadEntityFormDescriptor(ekey, org){
        return BaseServices.getRequest(["meta", org, ekey, "form.json"]);
    }

    loadModelJson(org, key) {
        ///meta/{org}/{key}/model.json
        return BaseServices.getRequest(["meta", org, key, "model.json"]);
    }

    loadEntityData(entity, id, org) {
        ///{org}/{stereotype}/{id}
        return BaseServices.getRequest([org, entity, id]);
    }

    createEntityData(key, data, org) {
        // POST /{org}/{stereotype}
        return $.ajax({
            type: "POST",
            url: BaseServices.url([org, key]),
            dataType: "json",
            contentType: "application/json",
            data: JSON.stringify(data)
        });
    }

    updateEntityData(key, id, data, org) {
        // PUT /{org}/{stereotype}/{id}
        return $.ajax({
            type: "PUT",
            url: BaseServices.url([org, key, id]),
            dataType: "json",
            contentType: "application/json",
            data: JSON.stringify(data)
        });
    }

    mergeEntityData(key, targetId, sourceId, data, org) {
        // PUT /{org}/{stereotype}/{targetId}?mergeFrom={sourceId}
        return $.ajax({
            type: "PUT",
            url: BaseServices.url([org, key, targetId]) + "?mergeFrom=" + sourceId,
            dataType: "json",
            contentType: "application/json",
            data: JSON.stringify(data)
        });
    }

    deleteEntityData(key, id, org) {
        return $.ajax({
            type: "DELETE",
            url: BaseServices.url([org, key, id]),
            dataType: "json",
            contentType: "application/json"
        });
    }

    saveActiveOrgs(keys) {
        ///user/active-organizations
        return $.ajax({
            type: "PUT",
            url: BaseServices.url("user/active-organizations"),
            dataType: "json",
            contentType: "application/json",
            data: JSON.stringify({keys})
        });
    }

    getFileInfo(org, impl, version, entity, recordId, field, fileId){
        return BaseServices.getRequest([org, impl, version, entity, recordId, "fields", field, "files", fileId]);
    }

    getFile(org, impl, version, entity, recordId, field, fileId, fileName){
        return BaseServices.getRequest([org, impl, version, entity, recordId, "fields", field, "files", fileId, fileName]);
    }

    createFileReader(){
        return new FileReader();
    }

    uploadFile(org, impl, version, entity, recordId, field, fileData, callBack, progressCallback){
        const url = BaseServices.url([org, impl, version, entity, recordId, "fields", field, "files"]);

        const reader = this.createFileReader();
        reader.onload = function(evt){
            const promise = $.ajax({
                url,
                type: "POST",
                data: evt.target.result,
                processData: false,
                contentType: fileData.type,
                headers: {
                    "X-Hybrid-Filename": fileData.name
                },
                xhr() {
                    const myXhr = $.ajaxSettings.xhr();
                    if (myXhr.upload != null) {
                        myXhr.upload.onprogress = progressCallback;
                    }
                    return myXhr;
                }
            });

            if (callBack != null) { return callBack(promise); }
        };

        return reader.readAsArrayBuffer(fileData);
    }

    // GET /v2/meta/{org}/{impl}/{version}/{entity}/mapping-src/{srcImpl}/{srcVersion}/{srcEntity}.json
    loadMappingJson(org, targetKey, srcKey) {
        return BaseServices.getRequest(["meta", org, targetKey, "mapping-src", `${srcKey}.json`]);
    }

    loadFieldData(key, shortname, org) {
        ///meta/{org}/{impl}/{version}/{entity}/fields/{field}
        return BaseServices.getRequest(
            [ "meta", org, key, "fields", shortname ],
            {},
            {
                background: true,
            },
        );
    }


    loadAbstractionAi(org, entityKey, shortname, payload) {
        ///{org}/{impl}/{version}/suggest/{field}
        const [impl, version] = entityKey.split("/");
        return BaseServices.jsonPost(
            [ org, impl, version, "suggest", shortname ],
            payload,
            {
                background: true,
                ignoreFailure: true,
            },
        );
    }

    loadDQRSummary(stereotype, q, code, p, ps, o, owners) {
        return BaseServices.getRequest(["dqr", stereotype.get("key")], {
            q,
            code,
            p,
            ps,
            o,
            owners,
        });
    }

    loadDQRDetail(stereotype, q, code, p, ps, o, owners) {
        return BaseServices.getRequest(["dqr", stereotype.get("key"), "fields"], {
            q,
            code,
            p,
            ps,
            o,
            owners,
        });
    }

    loadITManifest() {
        if (process.env.NODE_ENV === "production") {
            return Promise.reject();
        } else {
            const root = "test/it";
            return promisify($.ajax({
                type: "GET",
                url: root + "/manifest.txt",
                dataType: "text"
            })).then(list => ({
                root,
                tests: list
            }));
        }
    }

    loadMocksetManifest() {
        if (process.env.NODE_ENV === "production") {
            return Promise.reject();
        } else {
            const root = "mocks";
            return promisify($.ajax({
                type: "GET",
                url: root + "/manifest.txt",
                dataType: "text"
            }));
        }
    }

    getStereotypeForEntity(key, stereotypes) {
        const sts = this.getStereotypesForEntity(key, stereotypes);
        if (sts.length) {
            return sts[sts.length - 1];
        } else {
            return null;
        }
    }

    getStereotypesForEntity(key, stereotypes) {
        const sts = [];
        stereotypes.each(function(stereotype, i) {
            const creatables = stereotype.get("creatables");
            return creatables.each(function(creatable, j) {
                const versions = creatable.get("versions");
                return versions.each(function(version, k) {
                    const vk = version.get("key");
                    if(vk === key) {
                        return sts.push(stereotype);
                    }
                });
            });
        });
        return sts;
    }

    _putJsonPref(url, value) {
        return $.ajax({
            type: "PUT",
            url: BaseServices.url(url),
            dataType: "json",
            contentType: "application/json",
            data: JSON.stringify(value)
        });
    }

    saveLicensePreference(org, impl, pref, value) {
        if (BaseServices.__mocked) {
            // If we're mocked, saving the preference will always respond with
            // the static value. That precludes testing behavior based on the
            // updated preference, which is means you have to gen a mockset with
            // every potential preference value you want to test with (and use
            // separate tests per value too).
            return Promise.resolve(value);
        }
        return this._putJsonPref(["prefs", org, impl, pref], value);
    }

    saveOrgUserPreference(org, pref, value) {
        if (BaseServices.__mocked) {
            return Promise.resolve(value);
        }
        return this._putJsonPref(["user", "prefs", org, pref], value);
    }

    loadGxepInfo(org) {
        return BaseServices.getRequest(["gxep", org]);
    }

    saveGxepInfo(org, infos) {
        return this._putJsonPref(["gxep", org], infos);
    }

    createLockClient() {
        return promisify(BaseServices.jsonPost(
            ["lock"],
        ));
    }

    createLockSet(clientId, lockSet) {
        return promisify(BaseServices.jsonPost(
            ["lock", clientId, "set"],
            lockSet,
        ));
    }

    confirmLockSet(clientId, lockSetId) {
        return promisify(BaseServices.jsonPost(
            [ "lock", clientId, "set", lockSetId, "active" ],
            null,
            { type: "PUT", background: true },
        ));
    }

    releaseLockSet(clientId, lockSetId) {
        return promisify(BaseServices.jsonPost(
            [ "lock", clientId, "set", lockSetId ],
            null,
            { type: "DELETE" },
        ));
    }

    // I DO NOT return a Promise/thenable, and only sorta work on browsers w/o
    // full fetch support.
    deleteLockClient(clientId) {
        const url = this.url(["lock", clientId]);
        if (!window.fetch.polyfill && "keepalive" in new Request("")) {
            // noinspection JSIgnoredPromiseFromCall
            window.fetch(url, {
                method: "DELETE",
                credentials: "same-origin",
                keepalive: true,
            });
        } else {
            // no keepalive support...
            $.ajax({
                type: "DELETE",
                url,
                async: false,
            });
        }
    }

    url(path) { return BaseServices.url(path); }

}

export default new Services();



// WEBPACK FOOTER //
// ./app/lib/Services.js